<template>
  <section
    id="search-filters"
    v-accessible-menubar
    class="search-filters"
    role="menubar"
  >
    <EFilter
      v-for="({ id, type, hint, placeholder, options, size }) in filters"
      :key="id"
      :name="id"
      :type="type"
      :hint="hint"
      :placeholder="placeholder"
      :options="options"
      :size="size"
      :selected="getSelected(id)"
      :data-test-id="`search-filter-${id}`"
      class="search-filters__filter"
      @select="onFilterChange(id, $event)"
    />
    <EButton
      class="search-filters__clear"
      data-test-id="search-filters-clear"
      variant="bare"
      size="m"
      role="menuitem"
      :disabled="clearDisabled"
      @click="clearFilters"
    >
      {{ $t('common.header.dropdowns.filters.clear') }}
    </EButton>
  </section>
</template>

<script>
import { deleteHashParam, FILTERS_KEY } from '~~/search/common/js/universal/hash-params';
import EButton from '~~/common/vue/components/button';
import EFilter from '~~/common/vue/components/filter';

/**
 * Wrapper component for search filters.
 * Includes a 'clear all' button.
 */
export default {
  name: 'ESearchFilters',
  components: {
    EButton,
    EFilter,
  },
  props: {
    /**
     * A list of objects describing the filters
     */
    filters: {
      type: Array,
      required: true,
    },
  },
  computed: {
    filterIds() {
      return this.filters.map(({ id }) => id);
    },
    clearDisabled() {
      // disable clear button if no URL params match the filters
      return Object
        .keys(this.$route.query)
        .every((param) => !this.filterIds.includes(param));
    },
    urlParams() {
      return new URLSearchParams(window.location.search);
    },
  },
  methods: {
    clearFilters() {
      deleteHashParam(FILTERS_KEY);
      const url = new URL(window.location.href);
      this.filterIds.forEach(url.searchParams.delete, url.searchParams);

      url.searchParams.delete('p');
      window.location.assign(url.toString());
    },
    onFilterChange(filter, value) {
      const url = new URL(window.location.href);
      if (value !== '') {
        url.searchParams.set(filter, value);
      } else {
        url.searchParams.delete(filter);
      }

      url.searchParams.delete('p');
      window.location.assign(url.toString());
    },
    getSelected(filter) {
      return this.urlParams.get(filter);
    },
  },
};
</script>

<style lang="scss" scoped>
.search-filters {
  // on mobile, the filters container is scrollable
  // which causes the box shadow on individual filters to be cut off
  // so we use a negative margin + padding trick to compensate for this
  $shadow-compensation: $space-s;

  align-items: center;
  display: flex;
  margin-bottom: -$shadow-compensation;
  overflow: scroll;
  overflow-y: visible;
  padding: $space-s $space-s $shadow-compensation;
  scrollbar-width: none;
  width: 100%;

  @include desktop {
    padding: 0 0 0 $page-container-left-space-desktop;
    margin-bottom: -$space-l;
    overflow: visible;
  }
  // add extra scroll space to align filters with search results when scrolled
  // on Firefox, just a regular margin doesn't count towards the scroll width
  // but on Chrome it does, so we have to use a pseudo-element instead
  &::after {
    content: '';
    display: block;
    flex-shrink: 0;
    height: $space-s;
    width: $space-s;

    @include desktop {
      content: none;
    }
  }

  &:focus {
    outline: none;
  }

  & > * + * {
    margin-left: $space-1s;

    @include desktop {
      margin-left: $space-m;
    }
  }
}

.search-filters__clear {
  margin-left: $space-m;
  padding-left: 0;

  @include desktop {
    font-size: $font-s;
  }

  &:disabled {
    display: none;
  }
}

::v-deep .search-filters__filter .filter__button {
  animation: e-fade-in $timing-1s both;

  @include desktop {
    animation: none;
  }
}
</style>
